import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ConfirmModal.css';

/**
 * Lightweight centered confirmation modal — replaces window.confirm() /
 * window.prompt() with something that respects the app's visual language.
 *
 * For prompt-style flows (Refund, etc.) pass an input control via `children`;
 * the modal stays a pure wrapper and lets the caller own state.
 */
export interface ConfirmModalProps {
    open: boolean;
    title: string;
    /** Optional descriptive text shown above the children slot. */
    message?: ReactNode;
    /** Optional rich body — typically the input(s) for prompt-style modals. */
    children?: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    /** When true the confirm button is rendered with the danger style. */
    danger?: boolean;
    /** Disables the confirm button (e.g. validation failure on a prompt). */
    confirmDisabled?: boolean;
    /** Spinner-state for async confirm handlers. */
    busy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    /** Optional Cypress selector for the modal root. Defaults to `'confirm-modal'`. */
    dataCy?: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    open,
    title,
    message,
    children,
    confirmLabel,
    cancelLabel,
    danger = false,
    confirmDisabled = false,
    busy = false,
    onConfirm,
    onCancel,
    dataCy,
}) => {
    const { t } = useTranslation();
    const resolvedConfirmLabel = confirmLabel ?? t('common.save');
    const resolvedCancelLabel = cancelLabel ?? t('common.cancel');

    // Escape closes — matches the Drawer's behaviour so the UI feels consistent.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !busy) onCancel();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, busy, onCancel]);

    if (!open) return null;

    return (
        <div className="gs-confirm__overlay" onClick={() => !busy && onCancel()}>
            <div
                className="gs-confirm__modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="gs-confirm-title"
                data-cy={dataCy ?? 'confirm-modal'}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="gs-confirm__header">
                    <h3 id="gs-confirm-title">{title}</h3>
                </div>
                {(message || children) && (
                    <div className="gs-confirm__body">
                        {message && <div className="gs-confirm__message">{message}</div>}
                        {children}
                    </div>
                )}
                <div className="gs-confirm__footer">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        data-cy="confirm-modal-cancel-btn"
                        onClick={onCancel}
                        disabled={busy}
                    >
                        {resolvedCancelLabel}
                    </button>
                    <button
                        type="button"
                        className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
                        data-cy="confirm-modal-confirm-btn"
                        onClick={onConfirm}
                        disabled={busy || confirmDisabled}
                    >
                        {busy ? t('common.working') : resolvedConfirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
